import { InfiniteScrollObserver } from '../helpers/InfiniteScrollObserver';
import { useExtractedNotices } from '../hooks/useExtractedNotices';
import { selectTspOptions, selectTsps } from '../map/slice/mapSelectors';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { LoadingIndicator } from '../helpers/LoadingIndicator';
import { SearchIcon } from '@iconicicons/react';
import { MultiSelectSearch } from '../helpers/MultiSelect';
import { useGetTSPsQuery } from '../services/edgeApi';
import { DropdownMenu } from '../helpers/DropdownMenu';
import { ExtractedNotice } from '@/types/api/ExtractedNotice';
import { ReusableModal } from '../modals/ReusableModal';
import { toDateString } from '../utils/dateUtils';
import { getRawNoticeUrl } from '../notices/noticeSlice';

export type ExtractedNoticeTableProps = {
  loading: boolean;
  gasDate: string;
};

export const ExtractedNoticeTable: React.FC<ExtractedNoticeTableProps> = ({
  gasDate,
}) => {
  useGetTSPsQuery();
  const [search, setSearch] = useState('');
  const [rawNotice, setRawNotice] = useState<ExtractedNotice | null>(null);

  const tsps = useSelector(selectTsps);
  const tspOptions = useSelector(selectTspOptions);
  const [selectedTsps, setSelectedTsps] = useState<number[]>([]);

  const filters = useMemo(() => {
    return {
      tsps: selectedTsps,
    };
  }, [selectedTsps]);

  const {
    data: notices,
    isLoading,
    isFetching,
    exhausted,
    next,
  } = useExtractedNotices(search, gasDate, filters);

  const rawNoticePath = useMemo(() => {
    if (!rawNotice) {
      return '';
    }
    // @ts-ignore
    return getRawNoticeUrl(rawNotice);
  }, [rawNotice]);

  return (
    <div>
      <div className="py-3 mx-3 row">
        <div className="col-6">
          <div className="input-group">
            <span className="input-group-text">
              <SearchIcon />
            </span>
            <input
              type="text"
              placeholder="Search"
              className="form-control"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="col-3">
          <MultiSelectSearch
            placeholder="Pipelines"
            options={tspOptions}
            value={selectedTsps}
            onChange={(options) =>
              setSelectedTsps(options.map((option) => option.value))
            }
          />
        </div>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Actions</th>
            <th>Id</th>
            <th>Pipeline</th>
            <th>Notice Type</th>
            <th>Critical</th>
            <th>Posting Date</th>
          </tr>
        </thead>
        <tbody>
          {notices?.map((notice) => (
            <tr key={notice.id}>
              <td>
                <DropdownMenu>
                  <li>
                    <span
                      role="button"
                      className="dropdown-item"
                      onClick={() => {
                        setRawNotice(notice);
                      }}
                    >
                      Full Notice
                    </span>
                  </li>
                </DropdownMenu>
              </td>
              <td className="fw-bold">#{notice.id}</td>
              <td>{tsps[notice.tsp]?.name}</td>
              <td>{notice.notice_type}</td>
              <td>{notice.critical}</td>
              <td>
                {notice.posting_date || notice.effective_date ? (
                  toDateString(notice.posting_date ?? notice.effective_date)
                ) : (
                  <span className="text-muted">NA</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!isLoading && !exhausted && <InfiniteScrollObserver onScrollIn={next} />}
      {(isLoading || isFetching) && (
        <div className="d-flex justify-content-center align-items-center">
          <LoadingIndicator />
        </div>
      )}
      <ReusableModal
        size="xl"
        isOpen={rawNotice !== null}
        toggle={() => setRawNotice(null)}
        header={
          rawNotice
            ? `Notice for ${tsps[rawNotice.tsp]?.name} on ${toDateString(
                rawNotice.posting_date ?? new Date(),
              )}`
            : ''
        }
      >
        {rawNoticePath.endsWith('.pdf') || rawNoticePath.endsWith('.html') ? (
          <iframe
            title="pdf"
            src={rawNoticePath}
            width="100%"
            height="900px"
            style={{
              maxHeight: '80vh',
              backgroundColor: 'white',
            }}
            allow="autoplay"
          />
        ) : (
          'No raw data found.'
        )}
      </ReusableModal>
    </div>
  );
};
